import { ClientUser } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'
import { updaterSettingsApi } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/updater-settings.api'
import { userTelegramGroupApi } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/user-telegram-group.api'
import { useCallback, useState } from 'react'
import { showError, showSuccess } from 'utils/snackbar'

interface UseDriverTelegramGroupParams {
    driver?: ClientUser | null
}

export const useDriverTelegramGroup = ({ driver }: UseDriverTelegramGroupParams) => {
    const { setApplication, application } = useAuth()
    const [isEditing, setIsEditing] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const editGroupName = useCallback(
        async (newGroupName: string) => {
            if (!driver?.id || !newGroupName.trim()) {
                showError('Group name cannot be empty')
                return
            }

            if (!driver.telegramGroup) {
                showError('No Telegram group to edit')
                return
            }

            if (!application?.projectName) {
                showError('Project name not found')
                return
            }

            setIsSaving(true)
            try {
                const updatedTelegramGroup = driver.telegramGroup ? { ...driver.telegramGroup, groupName: newGroupName.trim() } : null

                // Update driver telegram group in the database
                await userTelegramGroupApi.updateTelegramGroup(driver.id, updatedTelegramGroup)

                // Refresh application state using updater settings API
                const updatedApplication = await updaterSettingsApi.updateSettings(application.projectName, {
                    ...application.temp?.updater,
                })

                setApplication(updatedApplication)
                setIsSaving(false)
                showSuccess('Group name updated successfully')
                return {
                    ...driver,
                    telegramGroup: updatedTelegramGroup || undefined,
                }
            } catch (error) {
                setIsSaving(false)
                showError('Failed to update group name')
                throw error
            }
        },
        [driver, application, setApplication]
    )

    const deleteGroup = useCallback(async () => {
        if (!driver?.id) {
            return
        }

        if (!application?.projectName) {
            showError('Project name not found')
            return
        }

        setIsDeleting(true)
        try {
            // Update driver telegram group in the database - send null to unset fields
            await userTelegramGroupApi.updateTelegramGroup(driver.id, null)

            // Refresh application state using updater settings API
            const updatedApplication = await updaterSettingsApi.updateSettings(application.projectName, {
                ...application.temp?.updater,
            })

            setApplication(updatedApplication)
            setIsDeleting(false)
            showSuccess('Telegram group disconnected successfully')
            return {
                ...driver,
                telegramGroup: undefined,
            }
        } catch (error) {
            setIsDeleting(false)
            showError('Failed to disconnect group')
            throw error
        }
    }, [driver, application, setApplication])

    return {
        editGroupName,
        deleteGroup,
        isEditing,
        isDeleting,
        isSaving,
        setIsEditing,
    }
}
